import React, { useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import { useSetBranchLocation } from "../../../api/queries/organizationQueries";
import { getApiErrorMessage } from "../../../utils/apiError";

const MIN_RADIUS = 20;
const MAX_RADIUS = 5000;

/** Has this branch a location (needed for the check-in radius rule)? */
export const hasBranchLocation = (b) =>
  b?.latitude !== null &&
  b?.latitude !== undefined &&
  b?.longitude !== null &&
  b?.longitude !== undefined;

/**
 * Set the branch's location and allowed check-in radius. Employees (except
 * those tagged "check in anywhere") can only check in within this radius.
 */
const BranchLocationDialog = ({ branch, onClose }) => {
  const [lat, setLat] = useState(
    hasBranchLocation(branch) ? String(branch.latitude) : "",
  );
  const [lng, setLng] = useState(
    hasBranchLocation(branch) ? String(branch.longitude) : "",
  );
  const [radius, setRadius] = useState(String(branch.radius || 200));
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState(null);
  const save = useSetBranchLocation();

  const latN = Number(lat);
  const lngN = Number(lng);
  const radiusN = Number(radius);
  const problem =
    lat === "" || lng === "" || Number.isNaN(latN) || Number.isNaN(lngN)
      ? "Enter latitude and longitude."
      : latN < -90 || latN > 90
        ? "Latitude must be between -90 and 90."
        : lngN < -180 || lngN > 180
          ? "Longitude must be between -180 and 180."
          : !(radiusN >= MIN_RADIUS && radiusN <= MAX_RADIUS)
            ? `Radius must be ${MIN_RADIUS}–${MAX_RADIUS} m.`
            : null;
  const mapUrl = !problem
    ? `https://www.google.com/maps?q=${latN},${lngN}`
    : null;

  const useHere = () => {
    if (!navigator.geolocation)
      return setError("This browser can't share its location.");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(6));
        setLng(pos.coords.longitude.toFixed(6));
        setLocating(false);
        setError(null);
      },
      () => {
        setLocating(false);
        setError(
          "Couldn't get your location. Allow location access in the browser, or type the coordinates.",
        );
      },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const onDone = (msg) => ({
    onSuccess: () => {
      toast.success(msg);
      onClose();
    },
    onError: (err) => {
      const m = err?.response?.data?.message;
      setError(
        Array.isArray(m)
          ? m.join(" ")
          : getApiErrorMessage(err, "Could not save the location."),
      );
    },
  });
  const submit = () =>
    save.mutate(
      { id: branch.id, latitude: latN, longitude: lngN, radius: radiusN },
      onDone("Branch location saved."),
    );
  const remove = () => {
    if (
      !window.confirm(
        "Remove this branch's location? Its employees won't be distance-checked any more.",
      )
    )
      return;
    save.mutate(
      { id: branch.id, latitude: null, longitude: null },
      onDone("Branch location removed."),
    );
  };

  return (
    <Dialog
      title="Branch location"
      subtitle={branch.branchName}
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          {hasBranchLocation(branch) && (
            <button
              type="button"
              className="btn btn-outline-danger profile-btn me-auto"
              onClick={remove}
              disabled={save.isPending}
            >
              Remove
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={save.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={submit}
            disabled={save.isPending || !!problem}
          >
            {save.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            Save
          </button>
        </>
      }
    >
      {error && <div className="alert alert-danger fs-085 py-2">{error}</div>}
      <p className="fs-080 text-slate-600">
        Employees of this branch can only check in within the radius (unless
        tagged "check in anywhere" in Attendance Settings).
      </p>
      <button
        type="button"
        className="btn btn-sm btn-outline-primary mb-3"
        onClick={useHere}
        disabled={locating}
      >
        {locating ? (
          <span
            className="spinner-border spinner-border-sm me-1"
            aria-hidden="true"
          ></span>
        ) : (
          <i className="bi bi-crosshair me-1"></i>
        )}
        Use my current location
      </button>
      <div className="row g-3">
        <div className="col-6">
          <label htmlFor="branch-lat" className="profile-label">
            Latitude <span className="text-danger">*</span>
          </label>
          <input
            id="branch-lat"
            type="number"
            step="0.000001"
            className="form-control"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            placeholder="14.554729"
          />
        </div>
        <div className="col-6">
          <label htmlFor="branch-lng" className="profile-label">
            Longitude <span className="text-danger">*</span>
          </label>
          <input
            id="branch-lng"
            type="number"
            step="0.000001"
            className="form-control"
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            placeholder="121.024445"
          />
        </div>
        <div className="col-6">
          <label htmlFor="branch-radius" className="profile-label">
            Allowed radius (m) <span className="text-danger">*</span>
          </label>
          <input
            id="branch-radius"
            type="number"
            min={MIN_RADIUS}
            max={MAX_RADIUS}
            step="10"
            className="form-control"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
          />
          <div className="fs-070 text-slate-500 mt-1">
            {MIN_RADIUS}–{MAX_RADIUS} m. Default 200.
          </div>
        </div>
        <div className="col-6 d-flex align-items-end">
          {mapUrl && (
            <a
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="fs-080 mb-2"
            >
              <i className="bi bi-geo-alt me-1"></i>Check on Google Maps
            </a>
          )}
        </div>
      </div>
      {problem && (lat !== "" || lng !== "") && (
        <div className="text-danger fs-075 mt-2">{problem}</div>
      )}
    </Dialog>
  );
};

export default BranchLocationDialog;
